/**
 * 日管控缓存工具函数
 * 提供统一的缓存访问接口
 */

import store from '@/store';
import { defaultTemplate } from '@/api/daily';
import { dailylyControlApi } from '@/api/control/dailyControl.api';

/**
 * 获取模板数据（带缓存）
 * @param {string} templateNo - 模板编号，如果不传则使用默认模板
 * @param {boolean} forceRefresh - 是否强制刷新，跳过缓存
 * @returns {Promise<object>} 模板数据
 */
export async function getTemplateWithCache(templateNo = null, forceRefresh = false) {
  const cacheKey = templateNo || 'default';

  // 如果不强制刷新，先尝试从缓存获取
  if (!forceRefresh) {
    const cachedData = store.getters['dailyControl/getTemplateByKey'](cacheKey);
    if (cachedData) {
      // console.log(`使用缓存的模板数据: ${cacheKey}`);
      return cachedData;
    }
  }

  // 缓存未命中或强制刷新，调用接口
  // console.log(`从接口获取模板数据: ${cacheKey}`);
  const params = templateNo ? { templateNo } : {};
  const response = await defaultTemplate(params);

  // 保存到缓存
  store.commit('dailyControl/setTemplateCache', { 
    key: cacheKey, 
    data: response 
  });

  return response;
}

/**
 * 获取用户列表（带缓存）
 * @param {boolean} forceRefresh - 是否强制刷新，跳过缓存
 * @returns {Promise<array>} 用户列表
 */
export async function getUserListWithCache(forceRefresh = false) {
  // 如果不强制刷新，先尝试从缓存获取
  if (!forceRefresh) {
    const cachedUsers = store.getters['dailyControl/getUserList'];
    if (cachedUsers && cachedUsers.length > 0) {
      // console.log('使用缓存的用户列表');
      return cachedUsers;
    }
  }

  // 缓存未命中或强制刷新，调用接口
  // console.log('从接口获取用户列表');
  const userData = await dailylyControlApi.getUser({
    page: 1,
    pageSize: 100000
  });

  const userList = userData.results.map(item => ({
    label: item.name,
    value: item.id
  }));

  // 保存到缓存
  store.commit('dailyControl/setUserList', userList);

  return userList;
}

/**
 * 清除所有日管控相关缓存
 */
export function clearDailyControlCache() {
  store.commit('dailyControl/clearAllCache');
  // console.log('已清除所有日管控缓存');
}

/**
 * 清除指定模板缓存
 * @param {string} templateNo - 模板编号
 */
export function clearTemplateCache(templateNo = null) {
  const key = templateNo || 'default';
  store.commit('dailyControl/clearTemplateCache', key);
  // console.log(`已清除模板缓存: ${key}`);
}

/**
 * 清除用户列表缓存
 */
export function clearUserListCache() {
  store.commit('dailyControl/clearUserList');
  // console.log('已清除用户列表缓存');
}

/**
 * 预加载常用数据
 * 在应用启动或用户登录后调用，提前加载常用数据
 */
export async function preloadDailyControlData() {
  // console.log('开始预加载日管控数据...');
  
  try {
    // 并发加载模板和用户列表
    await Promise.all([
      getTemplateWithCache(), // 默认模板
      getUserListWithCache()   // 用户列表
    ]);
    
    // console.log('日管控数据预加载完成');
    return true;
  } catch (error) {
    console.error('预加载日管控数据失败:', error);
    return false;
  }
}
